import React, { type HTMLAttributes, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import includes from "lodash/includes";

const VALID_CATEGORIES = ["hybrid", "indica", "sativa"];

/**
 * Displays a "tile" with the name of a strain, the symbol, and category.
 *
 * Design: https://www.figma.com/design/SrwUo71bJ4MwaQyTxQlkdc/Strain-tile-revamp?node-id=8-160&p=f&t=wVBiFhWvdzbihLZa-0
 */
const MAX_FONT_SIZE = 46;

const StrainTile: React.FC<
  {
    name: string;
    symbol: string;
    category?: string;
    className?: string;
  } & HTMLAttributes<HTMLDivElement>
> = ({ name, symbol, category, className, ...props }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const symbolRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState(MAX_FONT_SIZE);

  let categorySanitized = category?.toLowerCase();
  if (!includes(VALID_CATEGORIES, categorySanitized)) {
    categorySanitized = undefined;
  }

  useEffect(() => {
    if (containerRef.current && symbolRef.current) {
      setFontSize(
        Math.min(
          MAX_FONT_SIZE,
          Math.floor(
            (MAX_FONT_SIZE * containerRef.current.offsetWidth) /
              symbolRef.current.offsetWidth,
          ),
        ),
      );
      setLoading(false);
    }
  }, []);

  // I know the type says that the symbol shouldn't be null, but
  // for some reason this isn't enforced in the API/database so
  // this needs to be accounted for. (example as of Feb 2026: ethereal-og)
  if (!symbol) {
    console.error(
      `Strain "${name}" is missing a symbol (aka strain acronym; ex: 'Bd', 'Lcg')`,
    );
    return null;
  }

  return (
    <div
      className={classNames(
        "text-white rounded-[6px] w-[104px] h-[134px] p-[6px] overflow-hidden",
        {
          "bg-grey": !categorySanitized,
          "bg-strain-hybrid": categorySanitized === "hybrid",
          "bg-strain-indica": categorySanitized === "indica",
          "bg-strain-sativa": categorySanitized === "sativa",
        },
        className,
      )}
      role="presentation"
      {...props}
    >
      <div
        ref={containerRef}
        className={classNames(
          "flex flex-col-reverse justify-between h-full transition-opacity",
          {
            "opacity-0": loading,
          },
        )}
      >
        <div className="text-xs leading-[1.3] text-right line-clamp-3">
          {name}
        </div>
        <div className="text-center font-bold" style={{ fontSize }}>
          {/**
           * There are a large number (1k+) strains with a symbol with more than
           * three characters as of June 2025. We need to investigate regenerating
           * the symbol for these strains, and whether we can find enough unique
           * combinations for all of our published strains. For now, we'll cap the
           * output here to 3 characters. See: https://leafly.atlassian.net/browse/TECH-1289
           */}
          <span ref={symbolRef}>{String(symbol).substring(0, 3)}</span>
        </div>
        <div className="text-xs leading-[1.3] text-left">
          {capitalize(categorySanitized)}
        </div>
      </div>
    </div>
  );
};

export default StrainTile;
